import React from 'react';
import PropTypes from 'prop-types';
import Button from 'infrastructure/js/components/controls/Button/button';
import {FieldArray} from 'redux-form';
import cn from 'classnames';

import './list.scss';


const LIST_ITEM_DEFAULT_HEIGHT = 46;

export default class List extends React.PureComponent {

  constructor(props) {
    super(props);

    this.listRef = React.createRef();
  }

  ignoreAll = (event) => {
    event.preventDefault();
  }

  renderRemoveButton = (index, fields) => {
    if (this.props.isViewOnly) {
      return null;
    }

    return (
      <span className={cn('pl pl-x-input-close remove-btn', {disabled: (fields.length <= this.props.minFieldsCount)}) }
            onClick={() => { this.removeButtonClickHandler(index, fields); }}
            onKeyPress={this.ignoreAll}/>
    );
  }

  removeButtonClickHandler = (index, fields) => {
    fields.remove(index);

    this.props.onRemoveCallback?.(index);
  }

  renderCloneButton = (index, fields) => {
    let {isViewOnly, isCloneEnabled, maxItemsToRender} = this.props;

    if (isViewOnly || !isCloneEnabled) {
      return null;
    }

    const disabled =  (maxItemsToRender && fields.length >= maxItemsToRender);

    return (
      <span className={cn('pl pl pl-duplicate clone-btn', {'disabled': disabled}) }
            onClick={() => { this.cloneButtonClickHandler(index, fields); }}
            onKeyPress={this.ignoreAll}/>
    );
  }

  cloneButtonClickHandler = (index, fields) => {
    const fieldData = fields.get(index);
    if (!fieldData) return;

    fields.insert(index + 1, fieldData);

    this.props.onCloneCallback?.(index);
  }

  renderAddButton = (fields) => {

    let {isViewOnly, addButtonDisabled, addButtonLabel, maxItemsToRender} = this.props;

    if (isViewOnly) {
      return null;
    }

    const disabled = addButtonDisabled || (maxItemsToRender && fields.length >= maxItemsToRender);

    return (
      <Button id="add-row-btn" disabled={disabled} className="add-row" onKeyPress={this.ignoreAll} onClick={() => { this.addButtonClickHandler(fields); }}>
        <span className="pl pl-icon-add"/> {addButtonLabel}
      </Button>
    );
  }

  scrollToLastItem = () => {
    //Note: to get only the last direct child .list-item of listRef.current, not any nested .list-items (of some other nested lists)
    let lastItem = this.listRef?.current?.querySelector(':scope > .list-item:last-of-type');
    lastItem?.scrollIntoView({behavior: 'smooth'});
  }

  addButtonClickHandler = (fields) => {
    const { defaultItem = {}, onAddCallback } = this.props;

    fields.push(defaultItem);
    onAddCallback?.();
    setTimeout(this.scrollToLastItem, 0);
  }

  renderRows = ({fields , meta: {error}}) => {
    let {maxItemsToOverflow, itemRenderer, className, name} = this.props;

    let isOverflow = maxItemsToOverflow && fields.length > maxItemsToOverflow;

    return (
      <div className={cn('add-remove-list-container', className)}>
        <div className={cn('add-remove-list', {'list-overflow': isOverflow})}
             ref={this.listRef}
             style={isOverflow ? {maxHeight: maxItemsToOverflow * LIST_ITEM_DEFAULT_HEIGHT} : {}}
        >
          {fields.map((fieldName, index) => {
              let data = {
                id: fieldName,
                name: fieldName,
                index: index,
                fieldData: fields.get(index),
                allFieldsData: fields.getAll(),
                listName: name,
              };
              return (
                <div className="list-item" key={'list-item' + index}>
                  {itemRenderer?.(data)}
                  <div className='list-item-buttons-container'>
                    {this.renderRemoveButton(index, fields)}
                    {this.renderCloneButton(index, fields)}
                  </div>
                </div>);
            }
          )}
        </div>
        {this.renderAddButton(fields)}
      </div>
    )
  }

  render() {
    return (
      <FieldArray name={this.props.name} updateKey={this.props.updateKey} component={this.renderRows}/>
    )
  }
}

List.defaultProps = {
  isViewOnly: false,
  preSelectedItems: [],
  onAddCallback: null,
  onRemoveCallback: null,
  onCloneCallback: null,
  addButtonDisabled: false,
  maxItemsToRender: null,
  maxItemsToOverflow: 0,
  addButtonLabel: 'ADD',
  updateKey: '',
  minFieldsCount: 0,
  isCloneEnabled: false,
  className: '',
};

List.propTypes = {
  name: PropTypes.string.isRequired,
  isViewOnly: PropTypes.bool,
  preSelectedItems: PropTypes.array,
  onAddCallback: PropTypes.func,
  onRemoveCallback: PropTypes.func,
  onCloneCallback: PropTypes.func,
  itemRenderer: PropTypes.func.isRequired,
  addButtonDisabled: PropTypes.bool,
  maxItemsToRender: PropTypes.number,
  maxItemsToOverflow: PropTypes.number,
  addButtonLabel: PropTypes.string,
  updateKey: PropTypes.any,
  minFieldsCount: PropTypes.number,
  isCloneEnabled: PropTypes.bool,
  defaultItem: PropTypes.any,
  className: PropTypes.string,
};

